import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { invalidateForTask } from "@/lib/useTaskMutations";
import { Pill } from "./Pill";
import { DueDatePill } from "./DueDatePill";
import { Avatar } from "./Avatar";
import { toast } from "./Toast";
import { TASK_STATUS_LABEL, taskStatusOrder } from "@/lib/taskStatus";
import { priorityLabel, priorityColorVar } from "@/lib/taskPriority";
import { addDays, startOfDay } from "@/lib/format";
import type { TaskRow } from "@shared/types";
import type { TaskStatus } from "@shared/types";

export interface RowMember {
  id: string;
  name: string;
  image?: string | null;
}

function toYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Click-away popover: a trigger button + a menu that closes on outside click / Escape. */
function Popover({
  label,
  trigger,
  children,
}: {
  label: string;
  trigger: ReactNode;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <span className="row-pop" ref={ref}>
      <button
        type="button"
        className="row-pop-trigger"
        aria-label={label}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen((o) => !o);
        }}
      >
        {trigger}
      </button>
      {open && (
        <div className="row-pop-menu" role="menu">
          {children(() => setOpen(false))}
        </div>
      )}
    </span>
  );
}

/**
 * TaskRowRich — one task row with project + priority + status + due, plus inline
 * actions (status, reschedule, reassign, open, delete). The row navigates to the
 * task on click; controls stopPropagation. `canAct=false` (e.g. a watched task
 * you don't own) renders read-only chips and an Open-only overflow.
 */
export function TaskRowRich({
  task,
  projectName,
  projectColor,
  members,
  canAct = true,
}: {
  task: TaskRow;
  projectName?: string;
  projectColor?: string | null;
  members: RowMember[];
  canAct?: boolean;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const patch = useMutation({
    mutationFn: (body: Parameters<typeof api.patchTask>[1]) =>
      api.patchTask(task.id, body),
    onSuccess: () =>
      invalidateForTask(qc, { projectId: task.projectId, taskId: task.id }),
    onError: () => toast.error("Couldn't update the task. Try again."),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteTask(task.id),
    onSuccess: () =>
      invalidateForTask(qc, { projectId: task.projectId, taskId: task.id }),
    onError: () => toast.error("Couldn't delete the task. Try again."),
  });

  const open = () =>
    navigate({ to: "/tasks/$taskId", params: { taskId: task.id } });

  const today = startOfDay(new Date());
  const reschedules: { label: string; value: string | null }[] = [
    { label: "Today", value: toYMD(today) },
    { label: "Tomorrow", value: toYMD(addDays(today, 1)) },
    { label: "Next week", value: toYMD(addDays(today, 7)) },
    { label: "Clear due date", value: null },
  ];

  return (
    <div
      className={`task-row trow-rich${task.status === "done" ? " done" : ""}`}
      role="button"
      tabIndex={0}
      aria-label={`Open task: ${task.title}`}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter") open();
      }}
    >
      {/* Status */}
      {canAct ? (
        <Popover
          label={`Status: ${TASK_STATUS_LABEL[task.status]}`}
          trigger={
            <Pill variant={task.status} dot>
              {TASK_STATUS_LABEL[task.status]}
            </Pill>
          }
        >
          {(close) =>
            taskStatusOrder.map((s) => (
              <button
                key={s}
                type="button"
                role="menuitem"
                className="row-pop-item"
                aria-current={s === task.status}
                onClick={(e) => {
                  e.stopPropagation();
                  close();
                  if (s !== task.status) patch.mutate({ status: s });
                }}
              >
                <Pill variant={s as TaskStatus} dot>
                  {TASK_STATUS_LABEL[s]}
                </Pill>
              </button>
            ))
          }
        </Popover>
      ) : (
        <Pill variant={task.status} dot>
          {TASK_STATUS_LABEL[task.status]}
        </Pill>
      )}

      {/* Title + project */}
      <div className="trow-main">
        <div className="tr-title">{task.title}</div>
        {projectName && (
          <span
            className="trow-project"
            style={{ ["--chip" as string]: projectColor ?? "var(--muted)" }}
          >
            <span className="trow-project-dot" />
            {projectName}
          </span>
        )}
      </div>

      {/* Priority */}
      <span
        className="trow-prio"
        title={`Priority: ${priorityLabel(task.priority)}`}
      >
        <span
          className="trow-prio-dot"
          style={{ background: priorityColorVar(task.priority) }}
        />
        {priorityLabel(task.priority)}
      </span>

      {/* Due — click to reschedule */}
      {canAct ? (
        <Popover
          label={task.dueDate ? "Reschedule" : "Set due date"}
          trigger={
            task.dueDate ? (
              <DueDatePill due={task.dueDate} />
            ) : (
              <span className="trow-nodue">Set due</span>
            )
          }
        >
          {(close) => (
            <>
              {reschedules.map((r) => (
                <button
                  key={r.label}
                  type="button"
                  role="menuitem"
                  className="row-pop-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    close();
                    patch.mutate({ dueDate: r.value });
                  }}
                >
                  {r.label}
                </button>
              ))}
              <label className="row-pop-pick">
                Pick date
                <input
                  type="date"
                  className="input"
                  defaultValue={task.dueDate ?? ""}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const v = e.target.value;
                    close();
                    if (v) patch.mutate({ dueDate: v });
                  }}
                />
              </label>
            </>
          )}
        </Popover>
      ) : (
        task.dueDate && <DueDatePill due={task.dueDate} />
      )}

      {/* Overflow */}
      <Popover label="More actions" trigger={<span aria-hidden="true">⋯</span>}>
        {(close) => (
          <>
            <button
              type="button"
              role="menuitem"
              className="row-pop-item"
              onClick={(e) => {
                e.stopPropagation();
                close();
                open();
              }}
            >
              Open
            </button>
            {canAct && (
              <>
                <div className="row-pop-label">Reassign to</div>
                {members.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    role="menuitem"
                    className="row-pop-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      close();
                      patch.mutate({ assigneeId: m.id });
                    }}
                  >
                    <Avatar id={m.id} name={m.name} size={18} image={m.image} />
                    {m.name}
                  </button>
                ))}
                <button
                  type="button"
                  role="menuitem"
                  className="row-pop-item row-pop-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    close();
                    if (
                      window.confirm(
                        `Delete "${task.title}"? This can't be undone.`,
                      )
                    ) {
                      remove.mutate();
                    }
                  }}
                >
                  Delete task
                </button>
              </>
            )}
          </>
        )}
      </Popover>
    </div>
  );
}
